/**
 * Filters component renders site (and optionally contact) filter selectors with dynamic field
 * inputs based on the chosen filter type. It builds a payload and passes it back via
 * `handleFilterChange` so parent components can aggregate and execute searches.
 *
 * Data for dropdowns is sourced from slice in Redux (tags). Each selectable filter encodes both an id
 * and an input type in its `ObjectId` (e.g. `Address/text-field`,).
 * Based on that type, the appropriate input (text, select) is rendered by `FieldTypeGenerator`.
 *
 * After a user chooses a filter and provides a value, clicking "Add Filter" emits a
 * normalized object with field identifiers and values. "Clear Filters" resets existing detail filters,
 * while "Search" triggers `searchPerson` using the current accumulated filters.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.handleFilterChange Callback invoked with a filter payload when a new filter is added.
 * @param {Array<Object>} props.tableData Current table data; used to optionally reset form state when empty.
 * @param {Function} props.searchPerson Executes the search action using accumulated filters.
 * @param {Array<Object>} props.detailFilter Array of currently applied detailed filters (controls button disabled state).
 * @param {Function} props.clearDetailFilter Clears all currently applied detailed filters.
 * @returns {JSX.Element} Rendered filter UI.
 */

import { Col, Divider, Flex, Row, Space, Spin, Tooltip, Typography } from "antd";
import Button from "@ui/form-controls/button";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Select from "@ui/form-controls/select";
import FieldTypeGenerator from "../shared/field-type-generator";

import { SITE_FILTER_OPTIONS, CONTACT_FILTER_OPTIONS, personalOrWorkOptions } from "./constants";
import {
  FIELD_TYPES,
  trueFalseOptions,
  contactTypeOptions,
  CANDIDATE_STATUS
} from "../shared/constants";

import { detailClientSearchClear } from "@redux/actionCreators";

const { Title } = Typography;

const { TEXT_FIELD, SELECT_FIELD } = FIELD_TYPES;

const defaultSelectedField = {
  selectedFieldId: null, // e.g., "ContactType"
  selectedFieldName: null, // e.g., "Contact Type"
  selectedFieldType: null, // e.g., "text", "input-select"
  selectedFieldOptions: [], // e.g., [{ Name: <selectedFieldName>, ObjectId: <selectedFieldId/FieldType> }, ...]
  selectedFieldDropDownValue: null,
  selectedFieldTextValue: null
};

const Filters = ({ handleFilterChange, searchPerson, detailFilter, clearDetailFilter }) => {
  const dispatch = useDispatch();

  const [selectedSiteFilterField, setSelectedSiteFilterField] = useState(defaultSelectedField);
  const [contactSelectKey, setContactSelectKey] = useState(0);
  const [siteSelectKey, setSiteSelectKey] = useState(0);
  const [selectedContactFilterField, setSelectedContactFilterField] =
    useState(defaultSelectedField);

  const candidateModalReducer = useSelector(rdc => rdc?.candidateModalReducer, shallowEqual);
  const driverReducer = useSelector(state => state.driverReducer || null, shallowEqual);

  const {
    getGlobalSearch: { isLoading: getGlobalSearchLoading }
  } = candidateModalReducer;

  const {
    tagsListInfo: { resultData: tagsList, isLoading: tagsListLoading }
  } = driverReducer;

  const addFilter = filterType => {
    if (filterType === "SiteFilter") {
      const obj = {
        siteFilterData: {
          filterId: selectedSiteFilterField.selectedFieldId,
          dropDownValue: selectedSiteFilterField.selectedFieldDropDownValue,
          filterName: selectedSiteFilterField.selectedFieldName,
          textValue: selectedSiteFilterField.selectedFieldTextValue,
          fieldType: selectedSiteFilterField.selectedFieldType,
          filterType
        }
      };

      handleFilterChange(obj);
      setSelectedSiteFilterField(defaultSelectedField);
      // Force remount of Select components to clear any internal state of custom Select
      setSiteSelectKey(prev => prev + 1);
    } else if (filterType === "ContactFilter") {
      const obj = {
        contactFilterData: {
          filterId: selectedContactFilterField.selectedFieldId,
          dropDownValue: selectedContactFilterField.selectedFieldDropDownValue,
          filterName: selectedContactFilterField.selectedFieldName,
          textValue: selectedContactFilterField.selectedFieldTextValue,
          fieldType: selectedContactFilterField.selectedFieldType,
          filterType
        }
      };

      handleFilterChange(obj);
      setSelectedContactFilterField(defaultSelectedField);

      // Force remount of Select components to clear any internal state of custom Select
      setContactSelectKey(prev => prev + 1);
    }
  };

  const handleSiteFilter = filter => {
    if (!filter) return;

    const fieldDropDownOptions = {
      Tags: tagsList?.Data.map(item => ({
        Name: item.TagName,
        ObjectId: item.TagName
      })),
      Status: CANDIDATE_STATUS
    };

    const [fieldId, fieldType] = filter.split("/");

    const field = SITE_FILTER_OPTIONS.find(option => option.ObjectId.startsWith(`${fieldId}/`));

    setSelectedSiteFilterField({
      selectedFieldId: fieldId,
      selectedFieldName: field?.Name,
      selectedFieldType: fieldType,
      selectedFieldOptions: fieldDropDownOptions[fieldId] || null
    });
  };

  const handleContactFilter = filter => {
    if (!filter) return;

    const fieldDropDownOptions = {
      ContactType: contactTypeOptions,
      PersonalOrWork: personalOrWorkOptions,
      IsPrimary: trueFalseOptions,
      IsOptOut: trueFalseOptions,
      HasEmailAddresses: trueFalseOptions,
      HasPhoneNumbers: trueFalseOptions,
      HasAddresses: trueFalseOptions
    };

    const [fieldId, fieldType] = filter.split("/");
    const field = CONTACT_FILTER_OPTIONS.find(option => option.ObjectId.startsWith(`${fieldId}/`));

    setSelectedContactFilterField({
      selectedFieldId: fieldId,
      selectedFieldName: field?.Name,
      selectedFieldType: fieldType,
      selectedFieldOptions: fieldDropDownOptions[fieldId] || null
    });
  };

  const handleSiteFieldChange = ({ fieldValue, fieldType }) => {
    if (fieldValue) {
      const payload = {};
      if (fieldType === SELECT_FIELD) {
        payload.selectedFieldDropDownValue = fieldValue;
      } else if (fieldType === TEXT_FIELD) {
        payload.selectedFieldTextValue = fieldValue.target.value;
      }
      setSelectedSiteFilterField(prevState => ({
        ...prevState,
        ...payload
      }));
    }
  };

  const handleContactFieldChange = ({ fieldValue, fieldType }) => {
    if (fieldValue) {
      const payload = {};
      if (fieldType === SELECT_FIELD) {
        payload.selectedFieldDropDownValue = fieldValue;
      } else if (fieldType === TEXT_FIELD) {
        payload.selectedFieldTextValue = fieldValue.target.value;
      }
      setSelectedContactFilterField(prevState => ({
        ...prevState,
        ...payload
      }));
    }
  };

  // Disable "Add Filter" button if selected fields are incomplete
  const isFilterDisabled = (contactField, siteField) => {
    if (contactField && contactField.selectedFieldId) {
      if (
        contactField.selectedFieldType === SELECT_FIELD &&
        !contactField.selectedFieldDropDownValue
      ) {
        return true;
      } else if (
        contactField.selectedFieldType === TEXT_FIELD &&
        !contactField.selectedFieldTextValue
      ) {
        return true;
      }
      return false;
    }

    if (siteField && siteField.selectedFieldId) {
      if (siteField.selectedFieldType === SELECT_FIELD && !siteField.selectedFieldDropDownValue) {
        return true;
      } else if (siteField.selectedFieldType === TEXT_FIELD && !siteField.selectedFieldTextValue) {
        return true;
      }
      return false;
    }

    return true;
  };

  // clear search data on unmount
  useEffect(() => {
    return () => {
      dispatch(detailClientSearchClear());
    };
  }, []);

  const loading = getGlobalSearchLoading || tagsListLoading;

  const hasUnaddedFilter =
    Boolean(selectedSiteFilterField.selectedFieldId) ||
    Boolean(selectedContactFilterField.selectedFieldId);

  return (
    <div className="global-search h-full">
      <Spin className="h-full" spinning={loading}>
        <Flex vertical className="filter-list h-full">
          {/* Site Filters */}
          <Col span={24}>
            <Title level={5}>Site Filter</Title>

            <Select
              placeholder="Search to Select"
              callback={filter => handleSiteFilter(filter)}
              value={selectedSiteFilterField.selectedFieldId}
              key={`site-select-${siteSelectKey}`}
              optionsArray={SITE_FILTER_OPTIONS}
              mode="single"
            />
          </Col>

          <FieldTypeGenerator
            selectedField={selectedSiteFilterField}
            handleFieldChange={handleSiteFieldChange}
          />

          <Row>
            <Button
              disabled={isFilterDisabled(null, selectedSiteFilterField)}
              variant="primary" className="mt-2"
              onClick={() => addFilter("SiteFilter")}
            >
              Add Site Filter
            </Button>
          </Row>

          <Divider className="mt-2 mb-2" />

          {/* Contact Filters */}
          <Col span={24}>
            <Title level={5}>Contact Filter</Title>

            <Select
              placeholder="Search to Select"
              callback={filter => handleContactFilter(filter)}
              value={selectedContactFilterField.selectedFieldId}
              key={`contact-select-${contactSelectKey}`}
              optionsArray={CONTACT_FILTER_OPTIONS}
              mode="single"
            />
          </Col>

          <FieldTypeGenerator
            selectedField={selectedContactFilterField}
            handleFieldChange={handleContactFieldChange}
          />

          <Row>
            <Button
              disabled={isFilterDisabled(selectedContactFilterField, null)}
              variant="primary" className="mt-2"
              onClick={() => addFilter("ContactFilter")}
            >
              Add Contact Filter
            </Button>
          </Row>

          {/* Search/ Add/ Clear Filters*/}
          <Row className="mt-4">
            <Space>
              <Button
                variant="outline"
                disabled={detailFilter.length === 0}
                onClick={clearDetailFilter}
              >
                Clear Filters
              </Button>

              <Tooltip title={hasUnaddedFilter ? "Add filter above prior to search" : null}>
                <span>
                  <Button
                    variant="primary"
                    disabled={detailFilter.length === 0 || hasUnaddedFilter}
                    onClick={searchPerson}
                  >
                    Search
                  </Button>
                </span>
              </Tooltip>
            </Space>
          </Row>
        </Flex>
      </Spin>
    </div>
  );
};
export default Filters;
